import type { Components, Theme } from '@mui/material/styles';
import { brand, radii, shadows, typography } from './tokens';

const primaryFocusRing = '0 0 0 3px rgba(37, 99, 235, 0.15)';

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        scrollBehavior: 'smooth',
        scrollbarGutter: 'stable',
      },
      body: {
        backgroundColor: brand.background,
        color: brand.textPrimary,
        fontFamily: typography.fontBody,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      '*': {
        boxSizing: 'border-box',
      },
      '::selection': {
        backgroundColor: brand.blue100,
        color: brand.blue900,
      },
    },
  },
  
  /*
   * Devanagari joins letters along a headline bar, which letter-spacing tears
   * apart, and its vowel marks rise above the line, which the tight Latin
   * heading line-heights clip. `:lang()` outranks the plain class an `sx`
   * prop compiles to, so this holds even where a page sets its own values.
   */
  MuiTypography: {
    styleOverrides: {
      root: {
        ':lang(hi) &': { letterSpacing: 'normal' },
      },
      h1: { ':lang(hi) &': { lineHeight: 1.4 } },
      h2: { ':lang(hi) &': { lineHeight: 1.4 } },
      h3: { ':lang(hi) &': { lineHeight: 1.4 } },
      h4: { ':lang(hi) &': { lineHeight: 1.45 } },
      h5: { ':lang(hi) &': { lineHeight: 1.5 } },
      overline: { ':lang(hi) &': { lineHeight: 1.8 } },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.9375rem',
        borderRadius: radii.md,
        padding: '12px 24px',
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:focus-visible': {
          outline: 'none',
          boxShadow: primaryFocusRing,
        },
      },
      containedPrimary: {
        backgroundColor: brand.blue600,
        color: brand.white,
        '&:hover': {
          backgroundColor: brand.blue700,
          transform: 'translateY(-1px)',
          boxShadow: shadows.md,
        },
        '&:active': {
          transform: 'translateY(0)',
        },
      },
      containedSecondary: {
        backgroundColor: brand.navy,
        color: brand.white,
        '&:hover': {
          backgroundColor: brand.navyLight,
          transform: 'translateY(-1px)',
          boxShadow: shadows.md,
        },
      },
      outlinedPrimary: {
        border: `1.5px solid ${brand.blue600}`,
        color: brand.blue600,
        backgroundColor: 'transparent',
        padding: '10px 22px',
        '&:hover': {
          backgroundColor: brand.blue50,
          borderColor: brand.blue700,
        },
      },
      outlinedSecondary: {
        border: `1.5px solid ${brand.border}`,
        color: brand.textPrimary,
        backgroundColor: brand.white,
        '&:hover': {
          backgroundColor: brand.backgroundAlt,
          borderColor: brand.borderDark,
        },
      },
      textPrimary: {
        color: brand.blue600,
        fontWeight: 500,
        '&:hover': {
          backgroundColor: brand.blue50,
        },
      },
      sizeLarge: {
        padding: '16px 32px',
        fontSize: '1rem',
        borderRadius: radii.lg,
      },
      sizeSmall: {
        padding: '8px 16px',
        fontSize: '0.875rem',
      },
    },
  },
  
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: radii.lg,
        boxShadow: shadows.card,
        border: `1px solid ${brand.border}`,
        backgroundColor: brand.white,
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      },
    },
  },
  
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: brand.white,
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: radii.lg,
      },
    },
  },
  
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      fullWidth: true,
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          minHeight: 48,
          borderRadius: radii.md,
          backgroundColor: brand.white,
          transition: 'border-color 200ms ease, box-shadow 200ms ease',
          '& fieldset': { 
            borderColor: brand.border, 
            borderWidth: '1.5px',
          },
          '&:hover fieldset': { 
            borderColor: brand.borderDark,
          },
          '&.Mui-focused fieldset': {
            borderColor: brand.blue600,
            borderWidth: '2px',
          },
          '&.Mui-focused': {
            boxShadow: primaryFocusRing,
          },
        },
        '& .MuiInputLabel-root': {
          fontWeight: 500,
          color: brand.textMuted,
          '&.Mui-focused': {
            color: brand.blue600,
          },
        },
        '& .MuiFormHelperText-root': {
          fontSize: '0.8125rem',
          marginTop: '6px',
        },
      },
    },
  },
  
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        minHeight: 48,
        borderRadius: radii.md,
        backgroundColor: brand.white,
        fontSize: '0.9375rem',
        '& fieldset': { 
          borderColor: brand.border, 
          borderWidth: '1.5px',
        },
        '&:hover fieldset': { 
          borderColor: `${brand.borderDark} !important`,
        },
        '&.Mui-focused fieldset': {
          borderColor: `${brand.blue600} !important`,
          borderWidth: '2px',
        },
        '&.Mui-focused': {
          boxShadow: primaryFocusRing,
        },
        '&.Mui-error fieldset': {
          borderColor: `${brand.error} !important`,
        },
      },
      input: {
        padding: '14px 16px',
        '&::placeholder': {
          color: brand.textMuted,
          opacity: 1,
        },
      },
    },
  },
  
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
      },
    },
  },
  
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        boxShadow: 'none',
      },
    },
  },
  
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: radii.lg,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radii.xs,
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
      filled: {
        backgroundColor: brand.backgroundAlt,
        color: brand.textSecondary,
        '&:hover': {
          backgroundColor: brand.border,
        },
      },
      outlined: {
        borderColor: brand.border,
        '&:hover': {
          backgroundColor: brand.backgroundAlt,
        },
      },
      colorPrimary: {
        backgroundColor: brand.blue50,
        color: brand.blue700,
        '&.MuiChip-outlined': {
          borderColor: brand.blue200,
        },
      },
      colorSuccess: {
        backgroundColor: brand.successLight,
        color: brand.success,
      },
      colorWarning: {
        backgroundColor: brand.warningLight,
        color: brand.goldDark,
      },
      colorError: {
        backgroundColor: brand.errorLight,
        color: brand.error,
      },
    },
  },
  
  MuiContainer: {
    defaultProps: {
      maxWidth: 'lg',
    },
    styleOverrides: {
      maxWidthLg: {
        maxWidth: '1280px !important',
      },
    },
  },
  
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: brand.border,
      },
    },
  },
  
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
        '&.Mui-selected': {
          backgroundColor: brand.blue50,
          color: brand.blue700,
          '&:hover': { 
            backgroundColor: brand.blue100,
          },
          '& .MuiListItemIcon-root': { 
            color: brand.blue600,
          },
          '& .MuiListItemText-primary': { 
            fontWeight: 600,
          },
        },
      },
    },
  },
  
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        color: brand.textMuted,
        minWidth: 56,
        '&.Mui-selected': {
          color: brand.blue600,
        },
      },
      label: {
        fontSize: '0.75rem',
        fontWeight: 500,
        '&.Mui-selected': {
          fontSize: '0.75rem',
          fontWeight: 600,
        },
      },
    },
  },
  
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 600,
        color: brand.textPrimary,
        backgroundColor: brand.backgroundAlt,
        borderBottomColor: brand.border,
      },
      body: {
        color: brand.textPrimary,
        borderBottomColor: brand.border,
      },
    },
  },
  
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: brand.textSecondary,
        transition: 'all 200ms ease',
        '&:hover': {
          color: brand.textPrimary,
          backgroundColor: brand.backgroundAlt,
        },
      },
    },
  },
  
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
      },
      standardSuccess: {
        backgroundColor: brand.successLight,
        color: brand.success,
      },
      standardWarning: {
        backgroundColor: brand.warningLight,
        color: brand.goldDark,
      },
      standardError: {
        backgroundColor: brand.errorLight,
        color: brand.error,
      },
      standardInfo: {
        backgroundColor: brand.infoLight,
        color: brand.info,
      },
    },
  },
  
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: brand.backgroundAlt,
      },
    },
  },
  
  MuiMenu: {
    defaultProps: {
      disableScrollLock: true,
    },
  },

  MuiPopover: {
    defaultProps: {
      disableScrollLock: true,
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: brand.navy,
        fontSize: '0.8125rem',
        padding: '8px 12px',
        borderRadius: radii.sm,
      },
    },
  },
  
  MuiBadge: {
    styleOverrides: {
      colorPrimary: {
        backgroundColor: brand.blue600,
      },
      colorError: {
        backgroundColor: brand.error,
      },
    },
  },
};
